import React, { FunctionComponent } from 'react';
import { PageProps, graphql } from 'gatsby';
import SectionsLoader from '@codersclan/gatsby-theme-airfleet/src/components/sectionsLoader';

// Components
import PostWebinar from '../../components/postWebinar';
import PostAsset from '../../components/postAsset';
import Breadcrumb from '../../components/breadcrumb';
import PostDetails from '../../@codersclan/gatsby-theme-airfleet/components/postDetails/postDetails';
import Head from '../../components/head';
import HeaderConfigurations from '../../components/headerConfigurations/headerConfigurations';

// Helpers
import { getCategory } from '../../helpers/postHelper';
import SearchSectionHelper from '../../helpers/searchSectionHelper';
import merge from 'lodash.merge';

// Hooks
import usePage from '../../hooks/usePage';
import useSEOPropsResolver from '../../hooks/useSEOPropsResolver';

// Theme
import Layout from '../../@codersclan/gatsby-theme-airfleet/components/layout/layout';
import { PostDetails as PostDetailsType } from '../../@codersclan/gatsby-theme-airfleet/@types/post';

interface Data {
  resourcesData: PostDetailsType;
  allWp: {
    nodes: Array<{
      themeGeneralSettings: {
        optionsGeneral: {
          relativePosts: {
            posts: Array<{
              postSelector: PostDetailsType;
            }>;
          };
          metaTags: {
            value: string;
          };
        };
      };
    }>;
  };
}

type Props = PageProps<Data>;

const ResourceTemplate: FunctionComponent<Props> = ({ data }) => {
  const { resourcesData, allWp } = data;

  const relativePosts =
    allWp?.nodes[0]?.themeGeneralSettings?.optionsGeneral?.relativePosts?.posts;
  const { metaTags } = allWp?.nodes[0]?.themeGeneralSettings?.optionsGeneral;

  // Memos
  const { name: category, slug } = React.useMemo(
    () => getCategory(resourcesData),
    [resourcesData],
  );
  const categorySlug = slug === 'reports' ? 'research' : slug;
  const resourcesPage = usePage('resources');
  const slugPage = usePage(categorySlug);

  const prefooterSection = React.useMemo(() => {
    if (slugPage?.templateAllPages?.sections) {
      return SearchSectionHelper.searchSectionHelper(
        slugPage.templateAllPages.sections,
        SearchSectionHelper.SECTIONS.PREFOOTER_CTA,
      );
    }
    return [];
  }, [slugPage]);

  let toRender = null;
  let { title } = resourcesData;
  const { uri } = resourcesData;

  let breadcrumbItems = [
    { label: 'Resources', uri: resourcesPage?.uri ?? '/resources' },
    { label: category, uri: slugPage?.uri ?? `/${categorySlug}` },
    { label: title, uri },
  ];

  switch (category) {
    case 'Webinars': {
      toRender = <PostWebinar webinar={resourcesData} />;
      if (resourcesData.sectionWebinar?.webinarOptions?.heroTitle?.title) {
        title = resourcesData.sectionWebinar.webinarOptions.heroTitle.title;
      }
      break;
    }
    default: {
      if (
        resourcesData?.tags?.nodes?.some(
          t => t.slug === 'gated' || t.slug === 'ungated',
        )
      ) {
        const isGated = resourcesData?.tags?.nodes?.some(
          t => t.slug === 'gated',
        );
        if (!isGated) {
          breadcrumbItems = [];
        }
        toRender = <PostAsset post={resourcesData} gated={isGated} />;
      } else {
        toRender = (
          <PostDetails post={resourcesData} relativePosts={relativePosts} />
        );
      }
      break;
    }
  }

  const seoProps = metaTags
    ? merge({}, useSEOPropsResolver(resourcesData), { metaTags: metaTags.value })
    : useSEOPropsResolver(resourcesData);

  return (
    <Layout>
      <Breadcrumb items={breadcrumbItems} />
      <HeaderConfigurations  {...resourcesData.sectionHeaderConfigurations} />
      <Head {...seoProps} />
      {toRender}
      {(prefooterSection?.length ?? 0) > 0 && (
        <SectionsLoader sections={prefooterSection!} />
      )}
    </Layout>
  );
};

export default ResourceTemplate;

export const postQuery: void = graphql`
  query($slug: String) {
    resourcesData: wpResource(slug: { eq: $slug }) {
      ...ResourceFragment
    }
    allWp {
      nodes {
        themeGeneralSettings {
          optionsGeneral {
            relativePosts {
              posts {
                postSelector {
                  ...BasicPostFragment
                }
              }
            }
            metaTags {
              value
            }
          }
        }
      }
    }
  }
`;
